import type {CommerceProductColor, CommerceProductSize} from '@/sanity/schemas/types';

import type {ClothingVariantForSelectorFragment} from './query';

type VariantProperty = NonNullable<
	ClothingVariantForSelectorFragment['properties']
>[number]['_type'];

function resolveVariant(
	properties: [VariantProperty, string | null | undefined][],
	variants: ClothingVariantForSelectorFragment[],
) {
	let matching: ClothingVariantForSelectorFragment[] = [...variants];

	for (const [type, selected] of properties) {
		if (selected) {
			const result = matching.filter((variant) => {
				const property = variant.properties?.find((property) => property._type === type);
				const hasColor =
					property &&
					'resolvedColor' in property &&
					property.resolvedColor?._id == selected;

				const hasSize =
					property &&
					'resolvedSize' in property &&
					property.resolvedSize?._id == selected;

				return hasColor || hasSize;
			});

			if (result.length > 0) {
				matching = [...result];
			}
		}
	}

	return matching.at(0);
}

function resolveVariantProperties(
	properties: [VariantProperty, string | null | undefined][],
	variants: ClothingVariantForSelectorFragment[],
) {
	const propertiesById = {
		sizeProperty: new Map<string, CommerceProductSize>(),
		baseColorProperty: new Map<string, CommerceProductColor>(),
		printColorProperty: new Map<string, CommerceProductColor>(),
		contrastColorProperty: new Map<string, CommerceProductColor>(),
	};

	properties.forEach(([type], i) => {
		// find variants matching the previous property if selected (ie. size -> baseColor -> printColor), otherwise use all variants
		const prevSelected = i > 0 ? properties[i - 1][1] : null;
		const prevType = prevSelected ? properties[i - 1][0] : null;

		const matching = prevSelected
			? variants.filter((variant) =>
					variant.properties?.find((property) => {
						const hasSize =
							prevType === 'sizeProperty' &&
							property._type === prevType &&
							property.resolvedSize?._id === prevSelected;
						const hasColor =
							prevType !== 'sizeProperty' &&
							property._type === prevType &&
							property.resolvedColor?._id === prevSelected;
						return hasSize || hasColor;
					}),
				)
			: [...variants];

		for (const variant of matching) {
			for (const property of variant.properties ?? []) {
				if (type === 'sizeProperty') {
					if (property._type === type && property.resolvedSize) {
						propertiesById['sizeProperty'].set(
							property.resolvedSize._id,
							property.resolvedSize,
						);
					}
				} else {
					if (property._type === type && property.resolvedColor) {
						propertiesById[type].set(
							property.resolvedColor._id,
							property.resolvedColor,
						);
					}
				}
			}
		}
	});

	return {
		sizes: Array.from(propertiesById.sizeProperty.values()),
		baseColors: Array.from(propertiesById.baseColorProperty.values()),
		printColors: Array.from(propertiesById.printColorProperty.values()),
		contrastColors: Array.from(propertiesById.contrastColorProperty.values()),
	};
}

/** To eliminate potential errors  */
export const findVariantForSelectedProperties = (
	variants: ClothingVariantForSelectorFragment[],
	selectedSizeId: string | null,
	selectedBaseColorId: string | null,
	selectedPrintColorId: string | null,
	selectedContrastColorId: string | null,
) => {
	return resolveVariant(
		[
			['sizeProperty', selectedSizeId],
			['baseColorProperty', selectedBaseColorId],
			['printColorProperty', selectedPrintColorId],
			['contrastColorProperty', selectedContrastColorId],
		],
		variants,
	);
};

export const findVariantPropertiesForDefaultProperties = (
	variants: ClothingVariantForSelectorFragment[],
	defaultSizeId: string | undefined,
	defaultBaseColorId: string | undefined,
	defaultPrintColorId: string | undefined,
	defaultContrastColorId: string | undefined,
) => {
	return resolveVariantProperties(
		[
			['sizeProperty', defaultSizeId],
			['baseColorProperty', defaultBaseColorId],
			['printColorProperty', defaultPrintColorId],
			['contrastColorProperty', defaultContrastColorId],
		],
		variants,
	);
};

/**
 * Returns available properties for the selection.
 * @param properties
 * @param variants
 */
export function findVariantPropertiesForSelectedProperties(
	properties: [VariantProperty, string | null | undefined][],
	variants: ClothingVariantForSelectorFragment[],
) {
	const propertiesById = {
		sizeProperty: new Map<string, CommerceProductSize>(),
		baseColorProperty: new Map<string, CommerceProductColor>(),
		printColorProperty: new Map<string, CommerceProductColor>(),
		contrastColorProperty: new Map<string, CommerceProductColor>(),
	};

	properties.forEach(([type], i) => {
		// find variants matching the previous property if selected (ie. size -> baseColor -> printColor), otherwise use all variants
		const prevSelected = i > 0 ? properties[i - 1][1] : null;
		const prevType = prevSelected ? properties[i - 1][0] : null;

		const matching = prevSelected
			? variants.filter((variant) =>
					variant.properties?.find((property) => {
						const hasSize =
							prevType === 'sizeProperty' &&
							property._type === prevType &&
							property.resolvedSize?._id === prevSelected;
						const hasColor =
							prevType !== 'sizeProperty' &&
							property._type === prevType &&
							property.resolvedColor?._id === prevSelected;
						return hasSize || hasColor;
					}),
				)
			: [...variants];

		for (const variant of matching) {
			for (const property of variant.properties ?? []) {
				if (type === 'sizeProperty') {
					if (property._type === type && property.resolvedSize) {
						propertiesById['sizeProperty'].set(
							property.resolvedSize._id,
							property.resolvedSize,
						);
					}
				} else {
					if (property._type === type && property.resolvedColor) {
						propertiesById[type].set(
							property.resolvedColor._id,
							property.resolvedColor,
						);
					}
				}
			}
		}
	});

	return {
		sizes: Array.from(propertiesById.sizeProperty.values()),
		baseColors: Array.from(propertiesById.baseColorProperty.values()),
		printColors: Array.from(propertiesById.printColorProperty.values()),
		contrastColors: Array.from(propertiesById.contrastColorProperty.values()),
	};
}
